import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import NoticeIcon from "@rsuite/icons/Notice";
import ArrowDownLineIcon from "@rsuite/icons/ArrowDownLine";
import ArrowUpLineIcon from "@rsuite/icons/ArrowUpLine";
import { colors } from "../../../../../ui/theme/colors";
import { shadows } from "../../../../../ui/theme/shadows";
import { DateTime } from "luxon";
import { AlertsModal } from "./AlertsModal";

export const CompactAlertSummary = ({
  alerts,
  systems,
  hhmSystems,
  currentView,
  recentValues,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showModalOpen, setShowModalOpen] = useState(false);
  const history = useHistory();

  // Filter alerts based on current view
  const filterAlertsByView = (alertsList) => {
    if (!currentView) return alertsList;

    return alertsList.filter((alert) => {
      const pgTable = alert.pg_table?.toLowerCase() || "";

      if (currentView === "MMB") {
        // Mag Data view: show mag and edu alerts (exclude log/hhm)
        return !pgTable.includes("log") && !pgTable.includes("hhm");
      } else if (currentView === "EDU") {
        // ENV Data view: show only edu alerts
        return pgTable.includes("edu") || pgTable.includes("mmb_edu");
      } else if (currentView === "HHM") {
        // Log Data view: show only log/hhm alerts
        return pgTable.includes("log") || pgTable.includes("hhm");
      }

      return true;
    });
  };

  const viewFilteredAlerts = useMemo(() => {
    return filterAlertsByView(alerts);
  }, [alerts, currentView]);

  // Calculate alert statistics
  const stats = useMemo(() => {
    const highAlerts = viewFilteredAlerts.filter((a) => a.severity === "high");
    const mediumAlerts = viewFilteredAlerts.filter(
      (a) => a.severity === "medium",
    );
    const lowAlerts = viewFilteredAlerts.filter((a) => a.severity === "low");

    // Get unique systems with alerts
    const uniqueSystems = [
      ...new Set(viewFilteredAlerts.map((a) => a.system_id)),
    ];

    // Get most recent alert
    const recentAlert =
      viewFilteredAlerts.length > 0
        ? viewFilteredAlerts.reduce((latest, alert) =>
            new Date(alert.inserted_at) > new Date(latest.inserted_at)
              ? alert
              : latest,
          )
        : null;

    return {
      total: viewFilteredAlerts.length,
      high: highAlerts.length,
      medium: mediumAlerts.length,
      low: lowAlerts.length,
      affectedSystems: uniqueSystems.length,
      recentAlert,
    };
  }, [viewFilteredAlerts]);

  // Get recent alerts for expanded view (always show top 5)
  const recentAlerts = useMemo(() => {
    return [...viewFilteredAlerts]
      .sort((a, b) => new Date(b.inserted_at) - new Date(a.inserted_at))
      .slice(0, 5);
  }, [viewFilteredAlerts]);

  const handleAlertClick = (alert) => {
    if (alert.pg_table.includes("log") || alert.pg_table.includes("hhm")) {
      history.push(`/system/hhm/${alert.system_id}`);
    } else {
      history.push(`/system/${alert.system_id}`);
    }
  };

  // Get raw current value from recentValues (for comparison)
  const getRawCurrentValue = (alert) => {
    if (!recentValues) return null;

    const systemData = recentValues.find(
      (system) => system.system_id === alert.system_id
    );

    if (!systemData) return null;

    const fieldAlias = alert.field_name_alias?.toLowerCase();

    if (fieldAlias === "compressor") {
      const value = systemData.comp_vib_status;
      if (value === undefined || value === null) return null;
      return value === "1" || value === 1 || value === true || value === "true" ? 1 : 0;
    } else if (fieldAlias === "he level" || fieldAlias?.includes("helium")) {
      if (systemData.helium_value === undefined || systemData.helium_value === null) return null;
      const system = systems?.find((s) => s.id === alert.system_id);
      if (system?.he_tank_volume && systemData.helium_units?.toUpperCase() === "LTRS") {
        return (systemData.helium_value / system.he_tank_volume) * 100;
      }
      return parseFloat(systemData.helium_value);
    } else if (fieldAlias === "he pressure" || fieldAlias?.includes("pressure") || fieldAlias?.includes("psia")) {
      if (systemData.pressure_value === undefined || systemData.pressure_value === null) return null;
      return parseFloat(systemData.pressure_value);
    }

    return null;
  };

  // Check if alert has resolved based on current value vs threshold
  const isAlertResolved = (alert) => {
    const currentValue = getRawCurrentValue(alert);
    if (currentValue === null) return false;

    const fieldAlias = alert.field_name_alias?.toLowerCase();
    const operator = alert.operator;

    // Can't determine resolution for delta alerts
    if (operator === "delta_neg" || operator === "delta_pos") return false;

    // Special handling for Compressor alerts
    // Alert triggers when compressor matches threshold state
    // Resolved when compressor is in the OPPOSITE state
    if (fieldAlias === "compressor") {
      const thresholdIsOff = alert.threshold === "false" || alert.threshold === "0" || alert.threshold === 0;
      const currentIsOff = currentValue === 0;
      // If alert was for compressor being OFF, resolved when it's ON (and vice versa)
      return thresholdIsOff ? !currentIsOff : currentIsOff;
    }

    const threshold = parseFloat(alert.threshold);
    if (isNaN(threshold)) return false;

    switch (operator) {
      case "less_than":
        return currentValue >= threshold;
      case "greater_than":
        return currentValue <= threshold;
      case "equals":
        return currentValue !== threshold;
      case "not_equals":
        return currentValue === threshold;
      default:
        return false;
    }
  };

  // Get current value from recentValues based on alert field
  const getCurrentValue = (alert) => {
    if (!recentValues) return null;

    // Don't show current value for delta/drop alerts
    if (alert.operator === "delta_neg" || alert.operator === "delta_pos") return null;

    const systemData = recentValues.find(
      (system) => system.system_id === alert.system_id
    );

    if (!systemData) return null;

    const fieldAlias = alert.field_name_alias?.toLowerCase();

    if (fieldAlias === "compressor") {
      const value = systemData.comp_vib_status;
      if (value === undefined || value === null) return null;
      // 0 = OFF, 1 = ON
      return value === "1" || value === 1 || value === true || value === "true"
        ? "ON"
        : "OFF";
    } else if (fieldAlias === "he level" || fieldAlias?.includes("helium")) {
      if (systemData.helium_value === undefined || systemData.helium_value === null) return null;
      // Check if we need to convert liters to percent
      const system = systems?.find((s) => s.id === alert.system_id);
      if (system?.he_tank_volume && systemData.helium_units?.toUpperCase() === "LTRS") {
        const percentValue = ((systemData.helium_value / system.he_tank_volume) * 100).toFixed(2);
        return `${percentValue} %`;
      }
      return `${parseFloat(systemData.helium_value).toFixed(2)} ${systemData.helium_units || alert.threshold_units || ""}`;
    } else if (fieldAlias === "he pressure" || fieldAlias?.includes("pressure") || fieldAlias?.includes("psia")) {
      if (systemData.pressure_value === undefined || systemData.pressure_value === null) return null;
      const decimals = systemData.pressure_units?.toLowerCase() === "mbar" ? 2 : 3;
      return `${parseFloat(systemData.pressure_value).toFixed(decimals)} ${systemData.pressure_units || alert.threshold_units || ""}`;
    }

    // Fallback to resolved_field_content if no match
    if (alert.resolved_field_content !== undefined && alert.resolved_field_content !== null) {
      return `${alert.resolved_field_content}${alert.resolved_field_units ? ` ${alert.resolved_field_units}` : ""}`;
    }

    return null;
  };

  const getCompressorCurrentValue = (alert) => {
    if (
      alert.field_name_alias === "Compressor" &&
      alert.resolved_field_content !== undefined
    ) {
      const value = alert.resolved_field_content;
      // 0 = OFF, 1 = ON
      return value === "1" || value === 1 || value === true || value === "true"
        ? "ON"
        : "OFF";
    }
    return null;
  };

  const formatTimestamp = (isoDate) => {
    const dt = DateTime.fromISO(isoDate);
    const formatted = dt.toFormat("MMM dd, 'at' HH:mm");
    const relative = dt.toRelative();
    return `${formatted} (${relative})`;
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case "high":
        return colors.error.main;
      case "medium":
        return colors.warning.main;
      case "low":
        return colors.neutral[600];
      default:
        return colors.primary.main;
    }
  };

  if (viewFilteredAlerts.length === 0) {
    return (
      <SummaryContainer>
        <CompactHeader>
          <HeaderIcon>
            <NoticeIcon
              style={{ fontSize: "20px", color: colors.success.main }}
            />
          </HeaderIcon>
          <HeaderContent>
            <HeaderTitle>No Active Alerts</HeaderTitle>
            <HeaderSubtitle>All systems operating normally</HeaderSubtitle>
          </HeaderContent>
        </CompactHeader>
      </SummaryContainer>
    );
  }

  return (
    <SummaryContainer>
      <CompactHeader onClick={() => setIsExpanded(!isExpanded)}>
        <HeaderIcon>
          <NoticeIcon style={{ fontSize: "20px" }} />
        </HeaderIcon>
        <HeaderContent>
          <HeaderTitle>
            {stats.total} Active Alert{stats.total !== 1 ? "s" : ""} Detected
          </HeaderTitle>
          <HeaderSubtitle>
            Affecting {stats.affectedSystems} system
            {stats.affectedSystems !== 1 ? "s" : ""} • Last 5 days
          </HeaderSubtitle>
        </HeaderContent>

        <StatsRow>
          {stats.high > 0 && (
            <StatBadge severity="high">
              <StatCount>{stats.high}</StatCount>
              <StatLabel>High</StatLabel>
            </StatBadge>
          )}
          {stats.medium > 0 && (
            <StatBadge severity="medium">
              <StatCount>{stats.medium}</StatCount>
              <StatLabel>Medium</StatLabel>
            </StatBadge>
          )}
          {stats.low > 0 && (
            <StatBadge severity="low">
              <StatCount>{stats.low}</StatCount>
              <StatLabel>Low</StatLabel>
            </StatBadge>
          )}
        </StatsRow>

        <ExpandIcon>
          {isExpanded ? <ArrowUpLineIcon /> : <ArrowDownLineIcon />}
        </ExpandIcon>
      </CompactHeader>

      {isExpanded && (
        <ExpandedContent>
          <ExpandedHeader>Recent Alerts</ExpandedHeader>
          {recentAlerts.map((alert, index) => (
            <AlertRow
              key={`${alert.system_id}-${alert.id}-${index}`}
              severity={alert.severity}
              resolved={isAlertResolved(alert)}
              onClick={() => handleAlertClick(alert)}
            >
              <AlertSeverityIndicator severity={alert.severity} resolved={isAlertResolved(alert)} />
              <AlertDetails>
                <AlertSystem>
                  {alert.cus_sys_id || alert.system_id}
                  <AlertSite>
                    {alert.site_name && ` • ${alert.site_name}`}
                  </AlertSite>
                </AlertSystem>
                <AlertMessage>
                  {alert.field_name_alias === "Compressor"
                    ? `${alert.field_name_alias} is ${alert.threshold === "false" || alert.threshold === 0 ? "OFF" : "ON"}!`
                    : alert.operator === "offline" ||
                        alert.operator === "frozen_host"
                      ? `${alert.field_name_alias} ${alert.threshold} ${alert.threshold_units}`
                      : alert.field_name_alias?.toLowerCase() === "he level" &&
                          alert.operator === "delta_neg"
                        ? `${alert.field_name_alias} dropped ${alert.threshold} ${alert.threshold_units}`
                        : alert.operator === "contains" ||
                            alert.operator === "composite_equals"
                          ? alert.field_name_alias
                          : alert.operator === "absent_null_data"
                            ? `${alert.field_name_alias}: ${alert.threshold} ${alert.threshold_units}`
                            : `${alert.field_name_alias} is ${alert.operator.replace(/_/g, " ")} ${alert.threshold} ${alert.threshold_units}`}
                </AlertMessage>
              </AlertDetails>
              {getCurrentValue(alert) && (
                <AlertCurrentValueContainer>
                  <AlertCurrentValueLabel>Current Value</AlertCurrentValueLabel>
                  <AlertCurrentValue severity={alert.severity} resolved={isAlertResolved(alert)}>
                    {getCurrentValue(alert)}
                  </AlertCurrentValue>
                </AlertCurrentValueContainer>
              )}
              <AlertTimeInfo>
                <AlertTime>
                  Last: {formatTimestamp(alert.inserted_at)}
                </AlertTime>
                {alert.count > 1 && alert.min_inserted_at && (
                  <AlertTime>
                    First: {formatTimestamp(alert.min_inserted_at)}
                  </AlertTime>
                )}
              </AlertTimeInfo>
              {alert.count > 1 && <AlertCount>×{alert.count}</AlertCount>}
            </AlertRow>
          ))}
          <ViewAllButton onClick={() => setShowModalOpen(true)}>
            View All {viewFilteredAlerts.length} Alerts
          </ViewAllButton>
        </ExpandedContent>
      )}

      {/* Alerts Modal */}
      <AlertsModal
        open={showModalOpen}
        onClose={() => setShowModalOpen(false)}
        alerts={alerts}
        systems={systems}
        hhmSystems={hhmSystems}
        currentView={currentView}
        recentValues={recentValues}
      />
    </SummaryContainer>
  );
};

const SummaryContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 24px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${shadows.lg};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: ${shadows.xl};
  }

  @media screen and (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const CompactHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 24px;
  cursor: pointer;
  gap: 16px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  @media screen and (max-width: 768px) {
    padding: 16px;
    gap: 12px;
    flex-wrap: wrap;
  }
`;

const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(0, 91, 148, 0.1);
  color: ${colors.primary.main};
  flex-shrink: 0;

  @media screen and (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const HeaderTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.neutral[700]};
  margin: 0;

  @media screen and (max-width: 768px) {
    font-size: 16px;
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 14px;
  color: ${colors.neutral[600]};
  margin: 0;

  @media screen and (max-width: 768px) {
    font-size: 13px;
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media screen and (max-width: 768px) {
    width: 100%;
    order: 3;
    justify-content: flex-start;
    margin-top: 8px;
  }
`;

const StatBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 16px;
  border-radius: 12px;
  background: ${(props) =>
    props.severity === "high"
      ? "rgba(220, 38, 38, 0.1)"
      : props.severity === "medium"
        ? "rgba(245, 166, 35, 0.1)"
        : "rgba(156, 163, 175, 0.1)"};
  border: 1px solid
    ${(props) =>
      props.severity === "high"
        ? "rgba(220, 38, 38, 0.3)"
        : props.severity === "medium"
          ? "rgba(245, 166, 35, 0.3)"
          : "rgba(156, 163, 175, 0.3)"};
  min-width: 80px;

  @media screen and (max-width: 768px) {
    padding: 6px 12px;
    min-width: 50px;
  }
`;

const StatCount = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.neutral[700]};
  line-height: 1;

  @media screen and (max-width: 768px) {
    font-size: 18px;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.neutral[600]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 4px;

  @media screen and (max-width: 768px) {
    font-size: 10px;
  }
`;

const ExpandIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.neutral[600]};
  font-size: 20px;
  margin-left: auto;
  flex-shrink: 0;

  @media screen and (max-width: 768px) {
    order: 2;
  }
`;

const ExpandedContent = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 16px 24px 20px;
  background: rgba(0, 0, 0, 0.02);
  animation: slideDown 0.3s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media screen and (max-width: 768px) {
    padding: 12px 16px 16px;
  }
`;

const ExpandedHeader = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral[600]};
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AlertRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto auto auto;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 8px;
  background: ${(props) =>
    props.resolved
      ? "rgba(34, 197, 94, 0.15)"
      : "rgba(255, 255, 255, 0.5)"};
  border: 1px solid ${(props) =>
    props.resolved
      ? "rgba(34, 197, 94, 0.3)"
      : "rgba(255, 255, 255, 0.3)"};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${(props) =>
      props.resolved
        ? "rgba(34, 197, 94, 0.25)"
        : "rgba(255, 255, 255, 0.7)"};
    transform: translateX(4px);
    box-shadow: ${shadows.sm};
  }

  &:last-of-type {
    margin-bottom: 12px;
  }

  @media screen and (max-width: 768px) {
    padding: 10px;
    gap: 10px;
  }
`;

const AlertSeverityIndicator = styled.div`
  width: 4px;
  height: 40px;
  border-radius: 2px;
  flex-shrink: 0;
  background: ${(props) =>
    props.resolved
      ? colors.success.main
      : props.severity === "high"
        ? colors.error.main
        : props.severity === "medium"
          ? colors.warning.main
          : colors.neutral[500]};
`;

const AlertDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  overflow: hidden;
`;

const AlertSystem = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral[700]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media screen and (max-width: 768px) {
    font-size: 13px;
  }
`;

const AlertSite = styled.span`
  font-weight: 400;
  color: ${colors.neutral[600]};
  font-size: 13px;

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
`;

const AlertMessage = styled.div`
  font-size: 13px;
  color: ${colors.neutral[600]};
  line-height: 1.4;

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
`;

const AlertCurrentValueContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  pointer-events: none;
`;

const AlertCurrentValueLabel = styled.div`
  font-size: 9px;
  font-weight: 500;
  color: ${colors.neutral[500]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;

  @media screen and (max-width: 768px) {
    font-size: 8px;
  }
`;

const AlertCurrentValue = styled.div`
  font-size: 18px;
  color: ${(props) => {
    if (props.resolved) return colors.success.main;
    switch (props.severity?.toLowerCase()) {
      case "high":
        return colors.error.main;
      case "medium":
        return colors.warning.main;
      case "low":
        return colors.neutral[600];
      default:
        return colors.primary.main;
    }
  }};
  font-weight: 700;
  text-align: center;
  white-space: nowrap;

  @media screen and (max-width: 768px) {
    font-size: 16px;
  }
`;

const AlertTimeInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;

  @media screen and (max-width: 768px) {
    gap: 2px;
  }
`;

const AlertTime = styled.div`
  font-size: 12px;
  color: ${colors.neutral[500]};
  white-space: nowrap;

  @media screen and (max-width: 768px) {
    font-size: 11px;
  }
`;

const AlertCount = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  background: ${colors.primary.main};
  color: white;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
`;

const ViewAllButton = styled.button`
  width: 100%;
  padding: 10px;
  border: 1px solid rgba(0, 91, 148, 0.3);
  border-radius: 8px;
  background: rgba(0, 91, 148, 0.05);
  color: ${colors.primary.main};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 91, 148, 0.1);
    border-color: rgba(0, 91, 148, 0.5);
  }
`;
